/**
 * useIssueReports - Fetch and manage issue reports for the dashboard
 *
 * @example
 * ```tsx
 * const { data: issues, isLoading } = useIssueReports({ status: 'needs_review' });
 * const updateStatus = useUpdateIssueStatus();
 * updateStatus.mutate({ issueId: 'abc123', status: 'approved' });
 * ```
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import { httpsCallable } from "../../lib/firebase/authReadyCallable";
import { definedFields } from "../../lib/firebase/definedFields";
import { db, functions } from "../../config/firebase";
import { invokeNamedCallable } from "../../lib/telemetry/namedCallable";
import { useAuth } from "../../contexts/AuthContext";
import { signedInAsIssuePerson } from "../../lib/issueTriage/issuePerson";
import {
  APPROVABLE_STATUSES,
  reviewQueueStatus,
  statusAfterEdit,
} from "../../lib/issueTriage/reviewRule";
import { issueOwnerKeys } from "./issueOwnerKeys";
import type {
  IssuePerson,
  IssueReport,
  IssueStatus,
  IssueSeverity,
  IssueClassification,
} from "../../types/issue-triage";
import { logCallableFailure } from '../../lib/logCallableFailure';

// ============================================================================
// QUERY KEY FACTORY
// ============================================================================

export const issueReportKeys = {
  all: ["issueReports"] as const,
  lists: () => [...issueReportKeys.all, "list"] as const,
  list: (filters: IssueReportFilters) => [...issueReportKeys.lists(), filters] as const,
  details: () => [...issueReportKeys.all, "detail"] as const,
  detail: (id: string) => [...issueReportKeys.details(), id] as const,
};

// ============================================================================
// TYPES
// ============================================================================

export interface IssueReportFilters {
  status?: IssueStatus | IssueStatus[];
  severity?: IssueSeverity | IssueSeverity[];
  classification?: IssueClassification;
  limit?: number;
}

interface UseIssueReportsOptions {
  filters?: IssueReportFilters;
  enabled?: boolean;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetch issue reports with optional filtering
 */
export function useIssueReports(options: UseIssueReportsOptions = {}) {
  const { filters = {}, enabled = true } = options;
  const { user } = useAuth();

  return useQuery({
    queryKey: issueReportKeys.list(filters),
    queryFn: async () => {
      const constraints: QueryConstraint[] = [];

      // Status filter
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        if (statuses.length === 1) {
          constraints.push(where("status", "==", statuses[0]));
        } else if (statuses.length <= 10) {
          // Firestore 'in' limit is 10
          constraints.push(where("status", "in", statuses));
        }
      }

      // Severity filter
      if (filters.severity) {
        const severities = Array.isArray(filters.severity) ? filters.severity : [filters.severity];
        if (severities.length === 1) {
          constraints.push(where("severity", "==", severities[0]));
        } else if (severities.length <= 10) {
          constraints.push(where("severity", "in", severities));
        }
      }

      // Classification filter
      if (filters.classification) {
        constraints.push(where("classification", "==", filters.classification));
      }

      // Order by updated time (newest first)
      constraints.push(orderBy("updatedAt", "desc"));

      // Limit results
      if (filters.limit) {
        constraints.push(limit(filters.limit));
      }

      const issuesRef = collection(db, "issueReports");
      const q = query(issuesRef, ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as IssueReport[];
    },
    enabled: enabled && !!user,
    staleTime: 30000, // 30 seconds
  });
}

/** This issue as it is in EACH cached list (a list that does not hold it gives undefined). */
export function snapshotIssue(queryClient: QueryClient, issueId: string): [readonly unknown[], IssueReport | undefined][] {
  return queryClient
    .getQueriesData<IssueReport[]>({ queryKey: issueReportKeys.lists() })
    .map(([key, data]) => [key, data?.find((i) => i.id === issueId)]);
}

/**
 * Put THIS issue back as it was, list by list. Restoring a whole-board snapshot (or one list's copy into every list)
 * would also undo another card's change that is still in flight.
 */
export function restoreIssue(
  queryClient: QueryClient,
  issueId: string,
  before: [readonly unknown[], IssueReport | undefined][],
): void {
  for (const [key, previous] of before) {
    if (!previous) continue;
    queryClient.setQueryData<IssueReport[]>(key, (old) => old?.map((i) => (i.id === issueId ? previous : i)));
  }
}

/**
 * Update issue status with optimistic updates
 */
/** A fresh key per move; the server accepts [A-Za-z0-9_-]{1,64}. */
export function newStatusMutationId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function useUpdateIssueStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      issueId,
      status,
      notes,
      expectedStatus,
      expectedDeveloperNotes,
    }: {
      issueId: string;
      status: IssueStatus;
      notes?: string;
      /**
       * The status the caller saw (REQUIRED). The server refuses (failed-precondition,
       * "Already started by X") if the issue has moved on.
       */
      expectedStatus: IssueStatus;
      /**
       * Pickup CLAIM, part two: the direction text about to be copied ("" for
       * none). Refused if the stored direction changed since it was loaded.
       */
      expectedDeveloperNotes?: string;
    }) => {
      const fn = httpsCallable(functions, "issueTriage_updateStatus");
      // invokeNamedCallable, not a bare await: a FirebaseError carries only a
      // one-word code as its message, so an unnamed failure on this page became
      // an undiagnosable `FirebaseError: internal` (OFFICEHUB-CLIENT-69).
      // ONE key per move, minted outside the retry: if the answer is lost the transport re-sends the same body, and the server
      // answers success for a key whose move already landed instead of refusing it (error group e720c0e8a331cbbc).
      const mutationId = newStatusMutationId();
      const res = await invokeNamedCallable("issueTriage_updateStatus", () =>
        // definedFields: a missing note or direction is left OUT, never sent as null (TRIAGE-BOARD-400).
        fn(definedFields({ organizationId: user?.organizationId, issueId, status, notes, expectedStatus, expectedDeveloperNotes, mutationId })),
      );
      // The stage it LANDED in (the server may place a Reviewed request in Needs Review), so the screen
      // never says "moved to Reviewed" about an issue that is not there.
      const landed = (res?.data as { status?: unknown } | undefined)?.status;
      return { issueId, status: (typeof landed === "string" ? landed : status) as IssueStatus };
    },
    onMutate: async ({ issueId, status }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });

      // Snapshot THIS issue, list by list, for rollback
      const before = snapshotIssue(queryClient, issueId);

      // Optimistically update all list caches
      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          return old.map((issue) => {
            if (issue.id !== issueId) return issue;
            // Any move into in_progress from another stage records the starter
            // server-side; show it now so the card does not flash "unclaimed".
            const picker = signedInAsIssuePerson(
              user,
              queryClient.getQueryData<IssuePerson[]>(issueOwnerKeys.list())
            );
            const pickup =
              status === "in_progress" &&
              issue.status !== "in_progress" &&
              picker
                ? { pickedUpBy: picker, pickedUpAt: Timestamp.now() }
                : {};
            // Same rule as the server: only a ready issue lands in Reviewed.
            const effective = status === "reviewed" ? reviewQueueStatus(issue) : status;
            return { ...issue, status: effective, ...pickup };
          });
        }
      );

      return { before };
    },
    onError: (_err, { issueId }, context) => {
      if (context?.before) restoreIssue(queryClient, issueId, context.before);
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

/**
 * Approve an issue for implementation
 */
export function useApproveIssue() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ issueId, developerNotes }: { issueId: string; developerNotes?: string; notes?: string }) => {
      if (!user) {
        throw new Error("You must be logged in to approve issues");
      }

      const fn = httpsCallable(functions, "issueTriage_approve");
      await invokeNamedCallable("issueTriage_approve", () =>
        fn(definedFields({ organizationId: user.organizationId, issueId, developerNotes })),
      );

      // Trigger AI re-analysis in background (fire and forget).
      //
      // 🔴 THIS IS THE OFFICEHUB-CLIENT-69 SITE. The rejection used to be
      // console.error'd with the raw FirebaseError, and the console interceptor
      // captures the first Error argument AS the event with no fingerprint of
      // its own -- so it arrived in Sentry as `FirebaseError: internal`, on
      // /admin/issue-triage, with no stack and no callable name. reanalyzeIssue
      // wraps its whole AI + Firestore path in a catch that rethrows
      // HttpsError("internal", ...) (functions/src/issueTriage/reanalyzeIssue.ts),
      // so `internal` here is the EXPECTED shape of any failure inside it, not
      // an anomaly -- which is exactly why the event needed the name to be
      // actionable. The error is now named before it reaches the console.
      const reanalyze = httpsCallable(functions, "reanalyzeIssue");
      invokeNamedCallable("reanalyzeIssue", () => reanalyze({ issueId })).catch((err) => {
        logCallableFailure("Re-analysis failed:", err);
      });

      return { issueId };
    },
    onMutate: async ({ issueId, developerNotes }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });

      // Snapshot for rollback
      const previousLists = queryClient.getQueriesData({ queryKey: issueReportKeys.lists() });

      // Optimistically update with the server's rule: Approve lands in
      // Reviewed only when the issue is owned and has a direction; started
      // or finished work is never pulled back.
      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          return old.map((issue) => {
            if (issue.id !== issueId || !APPROVABLE_STATUSES.includes(issue.status)) return issue;
            return {
              ...issue,
              status: reviewQueueStatus({
                ...issue,
                developerNotes: developerNotes ?? issue.developerNotes,
              }),
            };
          });
        }
      );

      return { previousLists };
    },
    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logCallableFailure("Failed to approve issue:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

/**
 * Save the Developer Direction (developerNotes) on an issue.
 *
 * Optimistically writes the new notes into every cached list. The detail
 * panel and the Copy-for-Claude payload both render from these caches, so a
 * save that bypasses them (a raw callable) leaves every copy built from a
 * pre-annotation snapshot — the silent Developer Direction drop in #4304.
 */
export function useSaveDeveloperNotes() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      issueId,
      developerNotes,
      expectedDeveloperNotes,
    }: {
      issueId: string;
      developerNotes: string;
      /** The direction the caller saw ("" = none); the server refuses if a colleague has saved a different one since. */
      expectedDeveloperNotes?: string;
    }) => {
      if (!user) {
        throw new Error("You must be logged in to save developer notes");
      }
      const fn = httpsCallable(functions, "issueTriage_saveDeveloperNotes");
      await invokeNamedCallable("issueTriage_saveDeveloperNotes", () =>
        fn(definedFields({ organizationId: user.organizationId, issueId, developerNotes, expectedDeveloperNotes })),
      );
      return { issueId };
    },
    onMutate: async ({ issueId, developerNotes }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });

      const previousLists = queryClient.getQueriesData({ queryKey: issueReportKeys.lists() });

      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          // Mirror the server: the author is the caller (who the callable
          // stamps from auth), and an owned needs_review issue with a direction
          // moves to the reviewed pickup queue. A card Copy straight after the
          // save therefore already carries the direction AND its author.
          const author = signedInAsIssuePerson(
            user,
            queryClient.getQueryData<IssuePerson[]>(issueOwnerKeys.list())
          );
          return old.map((issue) => {
            if (issue.id !== issueId) return issue;
            const next = {
              ...issue,
              developerNotes,
              developerNotesBy: author,
              developerNotesUpdatedAt: Timestamp.now(),
            };
            // Promote when this makes it ready; demote when the direction is cleared.
            return { ...next, status: statusAfterEdit(next) };
          });
        }
      );

      return { previousLists };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

/**
 * Add a status note to an issue's comment thread
 *
 * @example
 * ```tsx
 * const addNote = useAddIssueNote();
 * addNote.mutate({ issueId: 'abc123', text: 'Checked logs, root cause confirmed.' });
 * ```
 */
export function useAddIssueNote() {
  const queryClient = useQueryClient();
  const { user, firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async ({ issueId, text }: { issueId: string; text: string }) => {
      if (!user || !firebaseUser) {
        throw new Error("You must be logged in to add notes");
      }

      const fn = httpsCallable(functions, "issueTriage_addNote");
      await invokeNamedCallable("issueTriage_addNote", () =>
        fn({ organizationId: user.organizationId, issueId, text }),
      );

      return { issueId };
    },
    onMutate: async ({ issueId, text }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });

      const previousLists = queryClient.getQueriesData({ queryKey: issueReportKeys.lists() });

      // Optimistic update: append note to cached issue
      const optimisticNote = {
        text,
        author: user?.displayName || user?.email || "Unknown",
        authorId: firebaseUser?.uid || "",
        timestamp: Timestamp.now(),
      };

      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          return old.map((issue) =>
            issue.id === issueId
              ? { ...issue, statusNotes: [...(issue.statusNotes || []), optimisticNote] }
              : issue
          );
        }
      );

      return { previousLists };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}

/**
 * Dismiss an issue
 */
export function useDismissIssue() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      issueId,
      reason,
      expectedStatus,
    }: {
      issueId: string;
      reason: "not_a_bug" | "duplicate" | "wont_fix" | "feature_request";
      /** The stage the caller saw (REQUIRED); the server refuses (failed-precondition) if the issue has moved on. */
      expectedStatus: IssueStatus;
    }) => {
      const fn = httpsCallable(functions, "issueTriage_dismiss");
      await invokeNamedCallable("issueTriage_dismiss", () =>
        fn({ organizationId: user?.organizationId, issueId, reason, expectedStatus }),
      );
      return { issueId };
    },
    onMutate: async ({ issueId }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });

      // Snapshot THIS issue, list by list, for rollback
      const before = snapshotIssue(queryClient, issueId);

      // Optimistically update
      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          return old.map((issue) =>
            issue.id === issueId ? { ...issue, status: "dismissed" as IssueStatus } : issue
          );
        }
      );

      return { before };
    },
    onError: (err, { issueId }, context) => {
      // Roll back THIS issue only (another card's move may still be in flight)
      if (context?.before) restoreIssue(queryClient, issueId, context.before);
      logCallableFailure("Failed to dismiss issue:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}
